import { Box, Button, FlexBox, Text, Tooltip } from '@dixa/lib-shared-habla';
import { useHasAccess } from 'lib-shared-access-control';
import { ampli } from 'lib-shared-tracking';
import { useCallback } from 'react';

import { useAppSelector } from 'utils/redux/useAppSelector';
import { CopilotTrialStatus, useCopilotTrial } from 'utils/useCopilotTrial';

const textContent = {
  [CopilotTrialStatus.Eligible]: {
    headings: [
      {
        props: {
          variant: 'heading-1',
          margin: '0 0 300',
        },
        content:
          'Introducing Agent Co-pilot: Elevate Your Team Support Experience with AI!',
      },
      {
        props: {
          variant: 'heading-2',
          margin: '0 0 200',
        },
        content: 'Unlock 30 days of trial: Empower your team to explore',
      },
    ],
    paragraphs: [
      {
        content:
          'Elevate your support team with our latest innovation, designed to streamline interactions and boost customer satisfaction.',
      },
    ],
  },
  [CopilotTrialStatus.TrialActive]: {
    headings: [
      {
        props: {
          variant: 'heading-3',
          margin: '0 0 400',
        },
        content: '🌟 Your Agent Co-pilot Trial is Now Active! 🌟',
      },
    ],
    paragraphs: [
      {
        content:
          "Ready to make Agent Co-pilot permanent? Request a contact from our team, and we'll reach out to you.",
      },
    ],
  },
  [CopilotTrialStatus.TrialExpired]: {
    headings: [
      {
        props: {
          variant: 'heading-1',
          margin: '0 0 300',
        },
        content: 'Transform Your Customer Support: Elevate Beyond the Trial',
      },
    ],
    paragraphs: [
      {
        props: {
          margin: '0 0 200',
        },
        content:
          "Your 30-day trial has ended—now is the moment to permanently enhance your customer support experience. Don't miss out on elevating your service to new heights.",
      },
      {
        content: "Request a contact from our team, and we'll reach out to you.",
      },
    ],
  },
};

export const CallToActionSection = (): JSX.Element => {
  const {
    status,
    startTrial,
    stopTrial,
    resetTrial,
    trialDaysLeft,
    openRequestFormContactForm,
    requestForContactSent,
    setRequestForContactSent,
  } = useCopilotTrial();
  const { userEmail } = useAppSelector((state) => ({
    userEmail: state.settings.profile.profile.email ?? '',
  }));
  const hasManageCopilotAccess = useHasAccess('settings.copilot');

  const requestCopilotModal = useCallback(async () => {
    ampli.agentCopilotTrialRequestContactClicked();
    const { isSent } = await openRequestFormContactForm();
    if (isSent) {
      setRequestForContactSent();
    }
  }, [openRequestFormContactForm, setRequestForContactSent]);

  const trialDaysLeftText = trialDaysLeft
    ? `${trialDaysLeft} days remaining`
    : '';

  const handleStartTrial = useCallback(() => {
    startTrial();
    ampli.agentCopilotTrialStarted();
  }, [startTrial]);

  const handleStopTrial = useCallback(() => {
    stopTrial();
    ampli.agentCopilotTrialStopped();
  }, [stopTrial]);

  return (
    <FlexBox gap="400" alignItems="flex-end">
      <FlexBox flex="1 0 400px">
        <Box width="100%" maxWidth="100%" minHeight="252px">
          <div style={{ aspectRatio: '16/9', display: 'flex' }}>
            <iframe
              style={{ border: 'none' }}
              width="100%"
              height="100%"
              src="https://www.youtube.com/embed/fCU1uPg_cmE?&amp;wmode=opaque"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          </div>
        </Box>
      </FlexBox>
      <FlexBox
        flex="1 1 550px"
        flexDirection="column"
        background="purple.100"
        borderRadius="100"
        padding="400"
        justifyContent="space-between"
      >
        <FlexBox flexDirection="column">
          {textContent[status].headings.map((heading, i) => (
            <Text.Heading key={i} color="purple.600" {...heading.props}>
              {heading.content}
            </Text.Heading>
          ))}
          {trialDaysLeftText && (
            <Text.Heading
              variant="heading-1"
              color="purple.600"
              margin="0 0 400"
            >
              {trialDaysLeftText}
            </Text.Heading>
          )}
          {textContent[status].paragraphs.map((paragraph, i) => (
            <Text.Paragraph key={i} {...paragraph.props}>
              {paragraph.content}
            </Text.Paragraph>
          ))}
        </FlexBox>
        <FlexBox gap="200" margin="300 0 0">
          {status === CopilotTrialStatus.Eligible &&
            ((hasManageCopilotAccess && (
              <Button onClick={handleStartTrial}>
                Start free trial for all agents
              </Button>
            )) || (
              <Text.Heading variant="heading-3">
                Ask for your lead to enable the trial for your team!
              </Text.Heading>
            ))}

          {[
            CopilotTrialStatus.TrialActive,
            CopilotTrialStatus.TrialExpired,
          ].includes(status) &&
            hasManageCopilotAccess && (
              <Tooltip
                content="Request for contact already sent"
                disabled={!requestForContactSent}
              >
                <Button
                  onClick={requestCopilotModal}
                  disabled={requestForContactSent}
                >
                  Request contact
                </Button>
              </Tooltip>
            )}
          {status === CopilotTrialStatus.TrialActive &&
            hasManageCopilotAccess && (
              <Button onClick={handleStopTrial} variant="tertiary">
                Stop trial
              </Button>
            )}
          {userEmail?.endsWith('@dixa.com') && (
            <Button icon="dixa" onClick={resetTrial} variant="danger">
              Reset trial
            </Button>
          )}
        </FlexBox>
        {status === CopilotTrialStatus.Eligible && hasManageCopilotAccess && (
          <FlexBox margin="300 0 0">
            <Text.Paragraph variant="4">
              Your trial begins instantly—no registration required. Cancel
              anytime.
            </Text.Paragraph>
          </FlexBox>
        )}
      </FlexBox>
    </FlexBox>
  );
};
